import { Component, ElementRef, forwardRef, inject, Input, ViewChild } from '@angular/core';

import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TippyInstance, TippyModule } from '@ngneat/helipopper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { isMobileView } from '@core/utils';
import { TimeWheelComponent } from '@mobile/_shared/sheets/time-wheel/time-wheel.component';
import { IconComponent } from '@shared/components/ui/icon/icon.component';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';

@UntilDestroy()
@Component({
  selector: 'app-time-picker-input',
  imports: [FormsModule, IconComponent, ReactiveFormsModule, TranslateModule, TippyModule],
  template: `
    <div class="input-icon-postfix">
      <input
        #timeInput="tippy"
        (click)="openTimePicker()"
        [disabled]="disabled"
        [id]="id"
        [name]="name"
        [placeholder]="placeholder"
        [readOnly]="readOnly"
        [required]="required"
        [tippy]="timePicker"
        [value]="value"
        className="popper-menu z-9999"
        placement="bottom"
        trigger="manual"
        type="text"
        variation="popper"
      />
      <app-icon iconName="clock" (click)="openTimePicker()"></app-icon>
    </div>
    <ng-template #timePicker let-hide>
      <div #timePickerContent class="selectable-list-popover">
        <ul class="selectable-list-popover__list">
          @for (time of timeArray; track time) {
            <li (click)="timeSelection(time)" [class.selected]="time === selectedTime" class="selectable-list-popover__list__item">
              <div class="icon">
                <!-- Show icon only for the selected item -->
                @if (time === selectedTime) {
                  <app-icon [cssStyle]="{ 'margin-top': '-4px' }" color="var(--orange)" iconName="check" [planeIcon]="true" size="20px"></app-icon>
                }
              </div>
              {{ time }}
            </li>
          }
        </ul>
      </div>
    </ng-template>
  `,
  styles: [
    `
      :host.ng-invalid.ng-dirty,
      :host.ng-invalid.ng-touched {
        input {
          border: 1px solid var(--red);
        }
      }

      .input-icon-postfix {
        input {
          padding-left: 16px;
        }
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerInputComponent),
      multi: true,
    },
  ],
})
export class TimePickerInputComponent implements ControlValueAccessor {
  @Input() value = '';
  @Input() placeholder = 'Select Time';
  @Input() disabled: boolean;
  @Input() required: boolean;
  @Input() name = 'time-selector';
  @Input() id = 'time-selector';
  @Input() readOnly = true;
  @ViewChild('timeInput', { static: true }) timeInput: TippyInstance;
  @ViewChild('timePickerContent') timePickerContent: ElementRef;
  timeArray: string[] = [];
  private readonly _bottomSheetService = inject(NgxBottomSheetService);
  private readonly _translate = inject(TranslateService);
  private _onChange: (value: any) => void;
  private _onTouched: () => void;
  private _previousSelectedTime: string;

  constructor() {
    this._setTimeArray();
    this.placeholder = this._translate.instant('Select Time');
  }

  get previousSelectedTime(): string {
    return this._previousSelectedTime;
  }

  @Input()
  set previousSelectedTime(value: string) {
    this._previousSelectedTime = value;
    this._setTimeArray(); // Recalculate the time array whenever previousSelectedTime changes
  }

  get selectedTime() {
    if (!this.value) return '0:0';
    const [hour, minute] = this.value.split(':');
    const time = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    return time;
  }

  writeValue(value: string): void {
    if (!value) this.value = '0:0';
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  timeSelection(time: string) {
    const [selectedHour, selectedMinute] = time.split(':').map(Number);
    const selectedTimeInMinutes = selectedHour * 60 + selectedMinute;
    if (this.previousSelectedTime) {
      const [prevHour, prevMinute] = this.previousSelectedTime.split(':').map(Number);
      const prevTimeInMinutes = prevHour * 60 + prevMinute;
      if (selectedTimeInMinutes <= prevTimeInMinutes) {
        return; // Don't allow selection of this time
      }
    }

    this.value = time;
    this.timeInput.hide();
    if (this._onChange) {
      this._onChange(time);
    }
  }

  openTimePicker() {
    if (this._onChange) {
      this._onChange(this.value);
    }

    if (!isMobileView()) {
      this.timeInput.show();
      this._scrollToSelected();
      return;
    }

    const timeWheelSheet = this._bottomSheetService.open(TimeWheelComponent, {
      height: '35%',
      showCloseButton: false,

      data: {
        currentSelectedTime: this.value,
        previousSelectedTime: this.previousSelectedTime,
        fixedHeaderHeight: 60,
      },
    });
    timeWheelSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (date) => {
        if (date) {
          this.value = date;
          if (this._onChange) {
            this._onChange(date);
          }
        }
      },
    });
  }

  private _setTimeArray() {
    const allTimes = [];
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 15) {
        const hour = i < 10 ? `0${i}` : `${i}`;
        const minute = j === 0 ? '00' : `${j}`;
        allTimes.push(`${hour}:${minute}`);
      }
    }
    // Filter based on previousSelectedTime
    if (this.previousSelectedTime) {
      const filterTime = this.previousSelectedTime.split(':').map(Number);
      const filterMinutes = filterTime[0] * 60 + filterTime[1];
      this.timeArray = allTimes.filter((time) => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes > filterMinutes;
      });
    } else {
      this.timeArray = allTimes;
    }
  }

  private _scrollToSelected() {
    setTimeout(() => {
      const dropdownContent = this.timePickerContent?.nativeElement;
      if (!dropdownContent) return;
      if (this.value) {
        const selectedElement = dropdownContent.querySelector('li[class*="selected"]');
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
      } else {
        dropdownContent.scrollTop = 0;
      }
    });
  }
}
